/**
 * DeliveriesSwitch - Switch a partner's deliveries on or off.
 *
 * Switching on asks first: from then on every property published is sent to
 * the partner automatically, which reaches outside Orchard. Switching off takes
 * effect at once, because stopping is always safe.
 */

import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/common/hooks/useAuth'
import { useToast } from '@/common/hooks/useToast'
import { getErrorMessage } from '@/common/utils/errors'
import { Switch } from '@/common/components/switch'
import { Button } from '@/common/components/button'
import { AlertDialog } from '@/common/components/alert-dialog'
import { updatePartner, type PartnerItem } from '../../api/partners'

interface DeliveriesSwitchProps {
  partner: PartnerItem
}

export function DeliveriesSwitch({ partner }: DeliveriesSwitchProps) {
  const { token } = useAuth()
  const { showSuccessToast, showErrorToast } = useToast()
  const queryClient = useQueryClient()
  const [confirmingEnable, setConfirmingEnable] = useState(false)
  const switchRef = useRef<HTMLButtonElement>(null)

  const update = useMutation({
    mutationFn: (enabled: boolean) => updatePartner(token ?? '', partner.id, { enabled }),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'partners'] })
      showSuccessToast(
        updated.enabled
          ? `Deliveries to ${updated.display_name} switched on.`
          : `Deliveries to ${updated.display_name} switched off.`,
      )
    },
    onError: (error: unknown) => showErrorToast(getErrorMessage(error)),
  })

  const handleCheckedChange = (checked: boolean) => {
    if (update.isPending) {
      return
    }
    if (checked) {
      setConfirmingEnable(true)
      return
    }
    update.mutate(false)
  }

  return (
    <>
      <Switch
        ref={switchRef}
        label={partner.enabled ? 'Deliveries on' : 'Deliveries off'}
        aria-label={`Deliveries to ${partner.display_name}`}
        checked={partner.enabled}
        onCheckedChange={handleCheckedChange}
        // Not disabled while saving: a disabled switch cannot take focus back
        // from the confirmation dialog. Changes are ignored until it settles.
        aria-busy={update.isPending}
      />

      <AlertDialog open={confirmingEnable} onOpenChange={(open) => !open && setConfirmingEnable(false)}>
        {/* The switch opens this dialog without being its trigger, so Radix has
            nowhere to return focus on close. */}
        <AlertDialog.Content
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            switchRef.current?.focus()
          }}
        >
          <AlertDialog.Title>Switch on deliveries to {partner.display_name}?</AlertDialog.Title>
          <AlertDialog.Description>
            Every property published while this is on will be sent to {partner.display_name}{' '}
            automatically. Switch it off again to stop sending new properties; links already
            delivered keep working.
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
            <Button
              variant="primary"
              onClick={() => {
                update.mutate(true)
                setConfirmingEnable(false)
              }}
            >
              Switch on deliveries
            </Button>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>
    </>
  )
}
